import { useCallback, useState } from 'react'

import { RequestStatus } from 'core/domain/Request'

import { type ShipmentCharge } from '../../domain/ShipmentCharges'
import {
  CancelShipmentChargeError,
  cancelShipmentCharge,
} from '../../use-cases/cancelShipmentCharge'

type CancelChargeState = {
  chargeUpdated?: ShipmentCharge
  cancelChargeStatus: RequestStatus
}

const initialCancelChargeState = {
  chargeUpdated: undefined,
  cancelChargeStatus: RequestStatus.Initial,
}

export function useCancelCharge({
  onSuccess,
  onError,
  onUnknownError,
}: {
  onSuccess: (charge?: ShipmentCharge) => void
  onError: (error: string, charge?: ShipmentCharge) => void
  onUnknownError: (error: string) => void
}) {
  const [{ chargeUpdated, cancelChargeStatus }, setCancelChargeState] =
    useState<CancelChargeState>(initialCancelChargeState)

  const cancelChargePatch = useCallback(
    async (
      shipmentUUID: string,
      chargeUUID: string,
      cancelReason: string,
      { signal }: AbortController
    ) => {
      try {
        setCancelChargeState(previous => ({
          ...previous,
          cancelChargeStatus: RequestStatus.Pending,
        }))

        const response = await cancelShipmentCharge(shipmentUUID, chargeUUID, cancelReason, {
          signal,
        })

        onSuccess(response)

        setCancelChargeState(previous => ({
          ...previous,
          chargeUpdated: response,
          cancelChargeStatus: RequestStatus.Done,
        }))
      } catch (error) {
        if (!signal.aborted) {
          let charge: ShipmentCharge | undefined = undefined

          if (error instanceof CancelShipmentChargeError) {
            onError(error.message, error.updatedCharge)
            charge = error.updatedCharge
          } else if (error instanceof Error) {
            onUnknownError(error.message)
          }
          setCancelChargeState(previous => ({
            ...previous,
            chargeUpdated: charge,
            cancelChargeStatus: RequestStatus.Error,
          }))
        }
      }
    },
    [onError, onUnknownError, onSuccess]
  )

  return {
    chargeUpdated,
    cancelChargeStatus,
    cancelChargePatch,
  }
}
